import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer, BookOpen, Heart, Sparkles, Search, Brain, Tractor } from "lucide-react";

const ISBN = "9781067688004";
const TITLE = "The Detective Diaries of Marchfield Meadows";
const AUTHOR = "Terata B.";

const SECTIONS = [
  {
    icon: BookOpen,
    color: "#2A9D8F",
    title: "Characters & Setting",
    questions: [
      "Who is the main character in the story?",
      "Where does the story take place?",
      "What clues show us what kind of farm Callie lives on?"
    ]
  },
  {
    icon: Search,
    color: "#E63946",
    title: "Plot & Problem-Solving",
    questions: [
      "What mystery does Callie need to solve in this story?",
      "What is the first clue she finds?",
      "Which animal friend helps her the most? How?",
      "What tools or skills does Callie use to solve the mystery?"
    ]
  },
  {
    icon: Brain,
    color: "#457B9D",
    title: "Critical Thinking",
    questions: [
      "If you were Callie, what clue would you look for next?",
      "What do you think the mystery teaches us about farm life?",
      "Why is it important to listen to all the animals' points of view?"
    ]
  },
  {
    icon: Tractor,
    color: "#E9C46A",
    title: "Farm Facts & Learning",
    questions: [
      "What fun fact did you learn about cows / chickens / pigs / etc.?",
      "How does this fact help you understand life on the farm?"
    ]
  },
  {
    icon: Heart,
    color: "#E63946",
    title: "Feelings & Values",
    questions: [
      "How does Callie show kindness or teamwork in the story?",
      "Why is it good to ask questions when things don't make sense?",
      "What would YOU like to discover if you visited Marchfield Meadows?"
    ]
  }
];

const ReadingGroupKitPage = () => {
  const handlePrint = () => window.print();

  return (
    <>
      {/* Print-specific styles */}
      <style>{`
        @media print {
          @page { size: A4; margin: 14mm; }
          body { background: white !important; }
          header, nav, footer, [data-no-print="true"] { display: none !important; }
          .print-page { background: white !important; padding: 0 !important; }
          .print-card { box-shadow: none !important; border: 2px solid #1D3557 !important; break-inside: avoid; page-break-inside: avoid; }
          .print-break { page-break-after: always; }
          a { color: #1D3557 !important; text-decoration: none !important; }
        }
        .answer-line {
          height: 28px;
          border-bottom: 1.5px dashed #1D3557;
          opacity: 0.35;
          margin: 10px 0;
        }
      `}</style>

      <div className="min-h-screen bg-[#FDFBF7] py-8 px-4 md:px-8 print-page">
        <div className="max-w-4xl mx-auto">
          {/* Toolbar — hidden on print */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8" data-no-print="true">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[#1D3557] font-bold hover:text-[#E63946] transition-colors"
              data-testid="back-home-link"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </Link>
            <button
              onClick={handlePrint}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="print-kit-btn"
            >
              <Printer className="w-5 h-5" />
              Print or Save as PDF
            </button>
          </div>

          {/* Cover header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="tactile-card print-card p-8 md:p-10 mb-8 bg-[#E9C46A]/20 text-center"
          >
            <div className="inline-block px-4 py-1.5 rounded-full bg-[#E63946] text-white font-bold text-sm mb-4 uppercase tracking-wider">
              Reading Group Kit
            </div>
            <h1 className="text-3xl md:text-5xl font-black text-[#1D3557] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              {TITLE}
            </h1>
            <p className="text-lg md:text-xl text-[#1D3557]/80 font-bold mb-2">by {AUTHOR}</p>
            <p className="text-sm text-[#1D3557]/70 font-mono">ISBN-13: {ISBN}</p>
            <div className="mt-6 max-w-2xl mx-auto">
              <p className="text-[#1D3557] font-medium leading-relaxed">
                Join Callie the Case-Solving Cow and her farm friends as they unravel mysteries across Marchfield Meadows. This kit is for teachers, librarians, parents, and book clubs — use the discussion questions below to bring the story to life.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-2 mt-6">
              <span className="px-3 py-1 rounded-full bg-[#2A9D8F]/20 text-[#1D3557] text-xs font-bold">Ages 5–9</span>
              <span className="px-3 py-1 rounded-full bg-[#457B9D]/20 text-[#1D3557] text-xs font-bold">Picture Book</span>
              <span className="px-3 py-1 rounded-full bg-[#E63946]/20 text-[#1D3557] text-xs font-bold">Mystery</span>
              <span className="px-3 py-1 rounded-full bg-[#E9C46A]/30 text-[#1D3557] text-xs font-bold">Farm / Animals</span>
              <span className="px-3 py-1 rounded-full bg-[#1D3557]/10 text-[#1D3557] text-xs font-bold">Teamwork &amp; Curiosity</span>
            </div>
          </motion.div>

          {/* How to use */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="tactile-card print-card p-6 mb-8 bg-[#457B9D]/10"
          >
            <h2 className="text-xl font-bold text-[#1D3557] mb-3 flex items-center gap-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              <Sparkles className="w-6 h-6 text-[#E63946]" />
              How to Use This Kit
            </h2>
            <ul className="list-disc list-inside space-y-2 text-[#1D3557] font-medium">
              <li><strong>Read together first.</strong> Cover-to-cover, then revisit favourite pages.</li>
              <li><strong>Pick &amp; choose questions.</strong> You don't need every question — pick what fits your group's age and time.</li>
              <li><strong>Let answers wander.</strong> Some questions are open-ended on purpose — there's no single "right" answer.</li>
              <li><strong>Use the lines provided</strong> to jot answers, or chat them out loud.</li>
            </ul>
          </motion.div>

          {/* Discussion Question Sections */}
          {SECTIONS.map((section, idx) => {
            const Icon = section.icon;
            const startNum = SECTIONS.slice(0, idx).reduce((sum, s) => sum + s.questions.length, 0) + 1;
            return (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 + idx * 0.05 }}
                className="tactile-card print-card p-6 md:p-8 mb-8"
                data-testid={`section-${idx + 1}`}
                style={{ borderLeftWidth: 8, borderLeftStyle: "solid", borderLeftColor: section.color }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: section.color }}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-[#1D3557]/60">Section {idx + 1}</p>
                    <h2 className="text-2xl font-bold text-[#1D3557] leading-tight" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                      {section.title}
                    </h2>
                  </div>
                </div>

                <ol className="space-y-5">
                  {section.questions.map((q, qIdx) => (
                    <li key={qIdx} className="text-[#1D3557]">
                      <div className="flex gap-3 mb-1">
                        <span
                          className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm text-white"
                          style={{ backgroundColor: section.color }}
                        >
                          {startNum + qIdx}
                        </span>
                        <p className="font-bold text-[#1D3557] flex-1 pt-1">{q}</p>
                      </div>
                      <div className="ml-11 mt-2">
                        <div className="answer-line"></div>
                        <div className="answer-line"></div>
                        <div className="answer-line"></div>
                      </div>
                    </li>
                  ))}
                </ol>
              </motion.div>
            );
          })}

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="tactile-card print-card p-6 bg-[#1D3557] text-white text-center"
          >
            <h3 className="text-xl font-bold mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Bring Callie home!
            </h3>
            <p className="font-medium mb-4 text-white/90">
              Search ISBN <span className="font-mono font-bold text-[#E9C46A]">{ISBN}</span> at any bookstore or library worldwide, or visit:
            </p>
            <p className="font-bold text-[#E9C46A] text-lg mb-2">farmsmartkids.com/where-to-buy</p>
            <p className="text-xs text-white/60 mt-4">
              © FarmSmartKids · The Detective Diaries of Marchfield Meadows by Terata B. · Feel free to copy &amp; share this kit with educators and reading groups.
            </p>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default ReadingGroupKitPage;
